import * as React from 'react'

import { Formik, Field, Form } from 'formik'
import styled from 'styled-components'

import type * as api from '@owl-nest/api-client/latest'
import { t, tc } from '@owl-nest/localize'
import * as money from '@owl-nest/money'
import * as models from '@owl-nest/models'
import * as shadow from '@owl-nest/shadow'
import * as hooks from '@owl-nest/hooks'
import * as services from '@owl-nest/services'
import * as plume from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'
import * as yup from '@owl-nest/validators'

import { StandaloneTipInput } from './components/input/StandaloneTipInput'
import { TipFrequencySwitch } from './components/TipFrequencySwitch'
import { TipInput } from './components/input/TipInput'
import { TipPreset } from './components/TipPreset'

// FIXME: remove this style and add @container css directly on TipView/TipFrenquence
// when we update style-component to v6
// don't forget to remove the two classname too
import './style.css'

export { TaxDeductionModal } from './components/TaxDeductionModal'

type TipProps = {
  allowNullValue?: boolean
  className?: string
  context?: string
  disableCta?: boolean
  frequency?: models.project.DonationFrequency
  handleFormSubmit: (values: FormValues) => void
  preFilledTip?: {
    amount?: number
    frequency?: models.project.DonationFrequency
  }
  presetsTip?: number[]
  project: api.Project
  removable?: boolean
  translations?: {
    placeholder?: string
    submit: React.ReactNode
  }
  withDisclaimer?: boolean
  withFromMention?: boolean
  withPresets?: boolean
  callout?: React.ReactNode
}

export type FormValues = {
  donation: number
  frequency?: models.project.DonationFrequency
  inputValue: string
}

function TipComponent({
  allowNullValue = false,
  className,
  callout,
  context = 'checkout',
  disableCta = false,
  frequency,
  handleFormSubmit,
  preFilledTip,
  presetsTip,
  project,
  removable,
  translations,
  withDisclaimer = false,
  withPresets = false,
  withFromMention = false,
}: TipProps): React.ReactElement<TipProps> {
  const allowedFrequency = models.project.allowedDonationFrequency(project)
  const currency = env.CURRENCIES_SYMBOL[project.currency].symbol
  const initialFrequency = allowedFrequency === 'all' ? frequency || 'one-time' : allowedFrequency
  const [donationFrequency, setDonationFrequency] = React.useState(initialFrequency)

  const [isInformativeModalOpen, setInformativeModalAsOpen] = React.useState(false)
  const setIsTaxDeductionModalOpen = hooks.useSetModal('DonationTaxDeductionInfo')
  const [amountAfterTaxReduction, setAmountAfterTaxReduction] = React.useState(0)

  const isDonationBasedProject = models.project.isDonationBased(project)
  const isMembershipProject = models.project.isMembership(project)

  const presets = getPresets()

  const schema = yup.object({
    donation: yup
      .numberWithComma()
      .min(0)
      .typeError(() => t('This field must be a number')),
  })
  const tracking = shadow.useTracking()
  const userConfig = env.useUserEnv()

  return (
    <TipView className={className}>
      <Formik
        initialValues={{
          donation: preFilledTip?.amount && presets?.includes(preFilledTip?.amount) ? preFilledTip?.amount : 0,
          frequency: isDonationBasedProject ? donationFrequency : undefined,
          inputValue:
            preFilledTip?.amount && (!withPresets || (withPresets && !presets?.includes(preFilledTip?.amount)))
              ? String(preFilledTip?.amount)
              : '',
        }}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {({ errors, resetForm, setFieldValue, values }) => {
          React.useEffect(() => {
            resetForm()
            if (isDonationBasedProject) {
              setFieldValue('frequency', donationFrequency)
            }
          }, [donationFrequency])

          React.useEffect(() => {
            if (values.donation > 0 && project.tax_deductible) {
              services.order
                .create(project.id, {
                  dry_run: true,
                  tip: values.donation,
                  // fake data that is required by the endpoint, but useless for
                  // a dry_run
                  payment_method: 'credit',
                  return_url: location.href,
                })
                .then((result) => {
                  result.next((response) => {
                    setAmountAfterTaxReduction(response.tax_deductible_total ?? 0)
                  })
                })
            }
          }, [values.donation])

          const handleInputChange = (value: string) => {
            setFieldValue('inputValue', value)
            setFieldValue('donation', Number(value.replace(/,/g, '.')))
          }

          const handlePresetChange = (value: number) => {
            setFieldValue('donation', value)
            setFieldValue('inputValue', '')
          }

          const resetSubmit = () => {
            setFieldValue('donation', undefined)
            setFieldValue('inputValue', '')
          }

          return (
            <Form>
              <TipContainerWrapper>
                {isDonationBasedProject && allowedFrequency === 'all' && (
                  <TipFrequencySwitch
                    className={'tip-frenquency-tip-component'}
                    frequency={donationFrequency}
                    setFrequency={setDonationFrequency}
                  />
                )}
                {withPresets ? (
                  <TipPresetsWrapper>
                    {presets.map((amount) => (
                      <TipPreset
                        amount={<money.MoneyDisplayer amount={amount} project={project} />}
                        checked={values.donation === amount && !values.inputValue}
                        id={`${context}-tip-preset-${amount}`}
                        key={amount}
                        name={`${context}_tip_preset_${amount}`}
                        onChange={() => {
                          handlePresetChange(amount)
                        }}
                      />
                    ))}

                    <TipInput
                      symbol={models.project.currencySymbol(project)}
                      error={errors.donation !== undefined}
                      onChange={handleInputChange}
                      selected={!!values.inputValue}
                      translations={translations}
                      value={values.inputValue}
                    />
                  </TipPresetsWrapper>
                ) : (
                  <StandaloneTipInput
                    symbol={models.project.currencySymbol(project)}
                    onChange={handleInputChange}
                    selected={!!values.inputValue}
                    translations={translations}
                    value={values.inputValue}
                    error={errors.donation}
                  />
                )}
              </TipContainerWrapper>

              {callout}

              <>
                <StyledCallToAction
                  type="submit"
                  disabled={disableCta || (!allowNullValue ? !values.donation && !values.inputValue : false)}
                >
                  {isDonationBasedProject ? (
                    <>
                      {values.donation > 0 || values.inputValue ? (
                        <>
                          {donationFrequency === 'monthly'
                            ? t('Donate %(amount)s per month', {
                                amount: t('%(currency)s%(amount)s', {
                                  amount: values.donation > 0 ? values.donation : values.inputValue,
                                  currency,
                                }),
                              })
                            : t('Donate %(amount)s', {
                                amount: t('%(currency)s%(amount)s', {
                                  amount: values.donation > 0 ? values.donation : values.inputValue,
                                  currency,
                                }),
                              })}
                        </>
                      ) : (
                        <>
                          {withFromMention ? (
                            <>
                              {donationFrequency === 'monthly'
                                ? t('Donate from %(amount)s per month', {
                                    amount: t('%(currency)s%(amount)s', {
                                      amount: 1,
                                      currency,
                                    }),
                                  })
                                : t('Donate from %(amount)s', {
                                    amount: t('%(currency)s%(amount)s', {
                                      amount: 1,
                                      currency,
                                    }),
                                  })}
                            </>
                          ) : (
                            <>{donationFrequency === 'monthly' ? t('Donate each month') : t('Donate')}</>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>{translations?.submit ? translations.submit : t('Back now')}</>
                  )}
                </StyledCallToAction>

                {project.tax_deductible && withPresets && (
                  <>
                    <TaxInfo kind="primary" tinted type="button" onClick={() => setIsTaxDeductionModalOpen(true)}>
                      <plume.glyphs.stroke.Percent size={18} />
                      {!values.donation
                        ? t('This donation is tax-deductible!')
                        : donationFrequency === 'monthly'
                          ? t('%(price)s after tax reduction', {
                              price: plume.accounting.localeMoney(amountAfterTaxReduction, {
                                currency: project.currency,
                                lang: userConfig.lang,
                              }),
                            })
                          : t('%(price)s after tax reduction', {
                              price: plume.accounting.localeMoney(amountAfterTaxReduction, {
                                currency: project.currency,
                                lang: userConfig.lang,
                              }),
                            })}
                    </TaxInfo>
                  </>
                )}

                {removable && (
                  <RemoveButton
                    kind="secondary"
                    onClick={() => {
                      resetSubmit()
                    }}
                  >
                    {t('Remove')}
                  </RemoveButton>
                )}
              </>

              {donationFrequency === 'monthly' && withDisclaimer && (
                <MonthlyDonationCallout customIcon={plume.glyphs.stroke.Reroll} type="info">
                  {tc(
                    '[strong: Monthly donation:] first payment today and then every month on this date. No commitment, you stop when you want.',
                    { strong: <strong /> },
                  )}
                </MonthlyDonationCallout>
              )}

              {withDisclaimer && (
                <>
                  {isDonationBasedProject && (
                    <>
                      <Disclaimer>
                        {tc(
                          "[strong: You are making a donation to a fundraiser.] Ulule does not guarantee that the project will be completed and is not responsible for the project team's commitment to their backers.",
                          { strong: <strong /> },
                        )}
                      </Disclaimer>
                      <plume.styles.copy.XS>
                        <ModalTriggerLink
                          kind="secondary"
                          onClick={() => setInformativeModalAsOpen(true)}
                          type="button"
                          underlined
                          withIcon
                        >
                          <plume.glyphs.stroke.Lock size={16} />
                          {t('Secure and final payment')}
                        </ModalTriggerLink>
                      </plume.styles.copy.XS>
                      <money.SecurePaymentModal
                        open={isInformativeModalOpen}
                        projectIsDonationBased
                        setOpen={setInformativeModalAsOpen}
                      />
                    </>
                  )}
                </>
              )}
            </Form>
          )
        }}
      </Formik>
    </TipView>
  )

  function getPresets(): number[] {
    if (isDonationBasedProject || isMembershipProject) {
      if (donationFrequency === 'monthly' || isMembershipProject) {
        return [5, 10, 20]
      }

      return presetsTip ?? [10, 25, 50]
    }

    return presetsTip ?? [25, 50, 100]
  }

  function onSubmit(values: FormValues) {
    // We add this condition because values.donation is undefined when a tip is removed
    if (values.donation) {
      tracking.ecommerce.track('add_to_cart', {
        currency: project.currency,
        value: values.donation,
        items: [
          {
            item_id: 'tip',
            item_name: 'tip',
            item_brand: models.project.name(project),
            price: values.donation,
            quantity: 1,
          },
        ],
      })
    }
    handleFormSubmit(values)
  }
}

export const Tip = styled(TipComponent)<TipProps>``

export const RemoveButton = styled(plume.LinkAsButton)``

const TaxInfo = styled(plume.LinkAsButton)`
  align-items: center;
  display: flex;
  flex: 0 0 100%;
  justify-content: center;
  margin-top: 16px;
  padding: 0;
  text-align: center;
  width: 100%;

  ${plume.glyphs.stroke.Percent} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    margin-right: 6px;
    margin-top: -1px;
  }
`

const Disclaimer = styled(plume.styles.copy.S)`
  margin: 20px auto;
`

const ModalTriggerLink = styled(plume.LinkAsButton)`
  margin: 0 auto;
  padding: 2px;
`

const MonthlyDonationCallout = styled(plume.Callout)`
  margin-top: 20px;

  ${plume.glyphs.stroke.Reroll} {
    color: ${plume.COLORS.PRIMARY_BLUE_700};
  }
`

const TipView = styled.div``

export const TipPresetsWrapper = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
  gap: 8px;
  flex-wrap: wrap;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    flex-wrap: nowrap;
    gap: 14px;
  }
`

const StyledCallToAction = styled(plume.Button)`
  margin-top: 16px;
  width: 100%;
`

export const TipContainerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  position: relative;
`

// FIXME: Used elsewhere, why? We need to clean this up.

export const InputRadioWrapper = styled.div`
  min-height: 50px;
  width: calc(20% - 5px);
`

const TipCurrency = styled(plume.styles.copy.S)`
  align-items: center;
  color: ${plume.COLORS.PRIMARY_GREY_500};
  display: flex;
  justify-content: space-between;
  padding: 0;
  width: 100%;

  ${money.CurrencyWrapper} {
    ${plume.styles.copy._mediumStyle};
    align-items: center;
    color: ${plume.COLORS.PRIMARY_GREY_500};
    display: flex;
    flex: 1;
    justify-content: center;
    min-width: 40px;
    padding: 13px 0;
  }
`

const TipInputField = styled(Field)`
  ${plume.styles.copy._smallStyle}
  border: 0px;
  box-sizing: border-box;
  height: 48px;
  padding: 15px 10px;
  width: 92%;

  &::placeholder {
    ${plume.styles.copy._smallStyle}
    color: ${plume.COLORS.PRIMARY_GREY_500};
  }

  &:required {
    box-shadow: none;
  }

  &:invalid {
    box-shadow: 0 0 0 transparent;
  }

  &:hover,
  &:focus {
    outline: none;
  }
`

type InputWrapperProps = {
  isSelected: boolean
}

export const InputWrapper = styled.div<InputWrapperProps>`
  align-items: center;
  border: 1px solid ${({ isSelected }) => (isSelected ? plume.COLORS.PRIMARY_BLUE_500 : plume.COLORS.PRIMARY_SAND_200)};
  box-sizing: border-box;
  color: ${({ isSelected }) => (isSelected ? plume.COLORS.PRIMARY_BLUE_500 : plume.COLORS.PRIMARY_SAND_200)};
  display: flex;
  justify-content: space-between;
  margin-right: 0;
  transition: 0.3s ease;
  width: 100%;

  ${TipCurrency}, ${money.CurrencyWrapper} {
    color: ${({ isSelected }) => (isSelected ? plume.COLORS.PRIMARY_BLUE_500 : plume.COLORS.PRIMARY_SAND_200)};
  }

  ${money.CurrencyWrapper} + ${TipInputField} {
    border-left: 1px solid
      ${({ isSelected }) => (isSelected ? plume.COLORS.PRIMARY_BLUE_500 : plume.COLORS.PRIMARY_SAND_200)};
  }

  ${TipInputField} + ${money.CurrencyWrapper} {
    border-left: 1px solid
      ${({ isSelected }) => (isSelected ? plume.COLORS.PRIMARY_BLUE_500 : plume.COLORS.PRIMARY_SAND_200)};
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-right: 9px;

    &:hover {
      border-color: ${plume.COLORS.PRIMARY_BLUE_500};
      cursor: pointer;
    }
  }
`

export { TipFrequencySwitch }
